import type * as React from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { navPillClass } from '@/lib/glass'
import type { UserProfile } from '@/types'
import { UserAvatar } from './UserAvatar'
import { useNavCompact } from '@/hooks/useNavCompact'

// Presentational primitives shared by the app-wide BottomNav and the festival-mode
// FestivalBottomNav. These own only the chrome (shell, item button, badge, profile
// avatar) — each nav keeps its own behavior (route tabs vs. view tabs, link vs.
// menu) by composing these rather than branching on a mode flag.

const EASE = 'duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'

// `relative` lifts the cells above the absolutely positioned indicator.
const ITEM_CLASS = cn(
  'group relative flex w-full flex-col items-center justify-center rounded-[22px] py-1.5 text-[11px] font-medium cursor-pointer transition-[color,padding]',
  'group-data-[compact]/nav:py-1',
  EASE,
)

/** Bottom navigation shell. Fixed by default; pass `fixed={false}` when a parent container owns positioning. */
export function NavBar({
  label,
  className,
  children,
  fixed = true,
  collapsible = true,
}: {
  label: string
  /** Compact the pill while scrolling down (useNavCompact). */
  collapsible?: boolean
  className?: string
  children: React.ReactNode
  fixed?: boolean
}) {
  const { listRef, indicator } = useActiveIndicator()
  const { compact, expand } = useNavCompact(collapsible)
  return (
    <nav
      data-compact={compact || undefined}
      className={cn(
        // The pill sits just above the bottom inset. Compacting drops ~20px (label row +
        // item padding), so lifting the bottom 10px keeps it shrinking toward its center.
        'group/nav chrome-passthrough px-4 pt-3 pb-[max(env(safe-area-inset-bottom),10px)] [[data-bottom-banner]+&]:pt-2',
        'transition-[padding] data-[compact]:pb-[calc(max(env(safe-area-inset-bottom),10px)_+_10px)]',
        // iOS: dip into the home-indicator inset, the way native floating tab bars
        // (Instagram) sit low in the curved corners, and sink further when compact.
        // Not Android — there the inset can be the 3-button nav bar.
        '[[data-platform=ios]_&]:pb-[max(calc(env(safe-area-inset-bottom)_-_10px),10px)]',
        '[[data-platform=ios]_&]:data-[compact]:pb-[max(calc(env(safe-area-inset-bottom)_-_14px),20px)]',
        EASE,
        fixed && 'fixed bottom-0 left-0 right-0 z-[60]',
        className,
      )}
      aria-label={label}
    >
      <ul ref={listRef} onClickCapture={compact ? expand : undefined} className={cn('relative mx-auto flex max-w-[min(28rem,100%)] items-stretch gap-0.5 rounded-[26px] p-1 transition-[max-width] group-data-[compact]/nav:max-w-[17rem]', EASE, navPillClass)}>
        <li aria-hidden="true" className="pointer-events-none absolute left-0 top-0">
          <span
            className={cn('block rounded-[22px] bg-primary/15', indicator.animate && cn('transition-[transform,width,height,opacity]', EASE))}
            style={{
              transform: `translate(${indicator.x}px, ${indicator.y}px)`,
              width: indicator.w,
              height: indicator.h,
              opacity: indicator.visible ? 1 : 0,
            }}
          />
        </li>
        {children}
      </ul>
    </nav>
  )
}

// One highlight that slides between tabs, tracking whichever cell has
// aria-current="page". Only a tab change animates; the first placement and any
// resize (the pill compacting) snap, since the pill's own transition already
// moves the cells every frame and a second easing on top trails behind it.
function useActiveIndicator() {
  const listRef = useRef<HTMLUListElement>(null)
  const [indicator, setIndicator] = useState({ x: 0, y: 0, w: 0, h: 0, visible: false, animate: false })

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return
    let placed = false
    const measure = (animate: boolean) => {
      const active = list.querySelector<HTMLElement>('[aria-current="page"]')
      setIndicator(prev => active
        ? { x: active.offsetLeft, y: active.offsetTop, w: active.offsetWidth, h: active.offsetHeight, visible: true, animate: animate && placed }
        : { ...prev, visible: false, animate: animate && placed })
      if (active) placed = true
    }
    measure(false)
    const mutations = new MutationObserver(() => measure(true))
    mutations.observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-current'] })
    const resize = new ResizeObserver(() => measure(false))
    resize.observe(list)
    return () => {
      mutations.disconnect()
      resize.disconnect()
    }
  }, [])

  return { listRef, indicator }
}

interface NavItemProps extends Omit<React.ComponentProps<'button'>, 'children'> {
  label: React.ReactNode
  active?: boolean
  /** Lucide icon shown above the label. Ignored when `visual` is provided. */
  icon?: LucideIcon
  /** Replaces the icon with a custom visual (e.g. a profile avatar). */
  visual?: React.ReactNode
  /** Optional count badge overlaid on the icon. */
  badge?: number
  /** data-testid for the badge — distinct per nav, since header and bottom nav both render one. */
  badgeTestId?: string
}

// A single bottom-nav cell. `ref`/extra props are forwarded to the button so it
// can be used as a Radix `asChild` trigger (e.g. DropdownMenuTrigger).
export function NavItem({
  label,
  active,
  icon: Icon,
  visual,
  badge,
  badgeTestId,
  className,
  ref,
  ...props
}: NavItemProps) {
  return (
    <button
      ref={ref}
      type="button"
      aria-current={active ? 'page' : undefined}
      className={cn(ITEM_CLASS, active ? 'text-primary' : 'text-muted-foreground hover:text-foreground', className)}
      {...props}
    >
      <span className="relative flex h-6 items-center justify-center">
        {visual ?? (Icon ? <Icon className={cn('size-5 transition-[stroke-width]', EASE, active && 'stroke-[2.5]')} aria-hidden="true" /> : null)}
        <NavBadge count={badge ?? 0} testId={badgeTestId} />
      </span>
      {/* Collapsing the row track (not display:none) lets the label fold away smoothly. */}
      <span className={cn('grid grid-rows-[1fr] transition-[grid-template-rows,opacity] group-data-[compact]/nav:grid-rows-[0fr] group-data-[compact]/nav:opacity-0', EASE)}>
        <span className="min-h-0 overflow-hidden pt-0.5">{label}</span>
      </span>
    </button>
  )
}

export function NavBadge({ count, testId }: { count: number; testId?: string }) {
  if (count <= 0) return null
  return (
    <span data-testid={testId} className={cn('absolute -top-1.5 -right-2 min-w-4 h-4 px-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold shadow-sm shadow-background/40')}>
      {count}
    </span>
  )
}

// The profile cell's visual: the user's avatar when signed in, a generic icon for
// guests. `active` adds the selected-tab ring (used by the route-based BottomNav).
export function NavProfileAvatar({
  isUser,
  profile,
  profileLoading,
  active,
}: {
  isUser: boolean
  profile: UserProfile | undefined
  profileLoading?: boolean
  active?: boolean
}) {
  return isUser ? (
    <UserAvatar loading={profileLoading} profile={profile} size="xs" className={cn(active && 'ring-2 ring-primary')} />
  ) : (
    <UserRound className="size-5" aria-hidden="true" />
  )
}
